import React, { Component, memo } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import axios from 'axios'
import Filter from '../components/Calendar/Filter'
import List from '../components/Calendar/List'
import NoResults from '../components/Calendar/NoResults'
import lunr from 'lunr'

import i18n from '../i18n'

class Calendar extends Component {
  constructor(props) {
    super(props)
    const nextYear = new Date().getFullYear() + 1
    this.state = {
      months: i18n.t('calendar.months', { returnObjects: true }),
      years: [nextYear.toString()],
      filters: {
        name: '',
        region: [],
        championshipType: [],
      },
      initialEvents: [],
      filteredEvents: [],
      hasFiltersApplied: false,
      loading: true,
      eventsIndex: null,
      showRegions: i18n.language === 'en',
      regionsWithEvents: [],
      availableChampTypes: [],
    }
  }

  componentDidMount() {
    this.setState({ loading: true })

    axios
    .get('/api/v1/event_groups/list.json', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    .then((response) => {
      this.setState({
        availableChampTypes: response.data.event_groups
      })
    })

    axios
      .get('/api/v1/events/calendar.json', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      .then((response) => {
        const minYear = new Date().getFullYear()
        const maxYear = new Date().getFullYear() + 2

        const allEvents = response.data.infosite_events
        const twoYearsEvents = this.flattenEventGroups(allEvents).filter(
          (ev) => ev.year >= minYear && ev.year < maxYear
        )
        const regionsWithEvents = allEvents
          .map((ev) => ev.region)
          .filter((value, index, self) => self.indexOf(value) === index)
        this.setState(
          {
            initialEvents: twoYearsEvents,
            filteredEvents: twoYearsEvents,
            loading: false,
            regionsWithEvents: regionsWithEvents,
          },
          () => window.requestAnimationFrame(() => this.scrollToCurrentMonth())
        )
        this.buildSearchIndex(twoYearsEvents)
      })
      .catch((error) => {
        this.setState({ loading: false })
        console.log(error)
      })
  }

  flattenEventGroups(events) {
    const groupedEvents = events.map((ev) => {
      let groups = ev.eventGroups.map((gp) => { return gp.name })
      ev.eventGroups = groups
      return ev
    })
    return groupedEvents
  }

  buildSearchIndex(events) {
    let idx = lunr(function () {
      this.ref('id')
      this.field('name')
      this.field('city')
      this.field('local')

      events.forEach(function (doc) {
        this.add(doc)
      }, this)
    })
    this.setState({ eventsIndex: idx })
  }

  scrollToRef(e) {
    // it would be nice to be able to scroll to exact months of the new year (when using mobile)
    let month = e.value ? e.value : e.target.dataset.month

    this.scrollToMonth(month, 'smooth')
  }

  scrollToMonth(month, behavior) {
    if (!month) return

    let element = document.getElementById(month)

    if (element == null) return

    let newPosition = 0
    if (this.state.years.includes(month)) {
      newPosition = element.offsetTop + 120
    } else {
      newPosition = element.offsetParent.offsetTop + 120
    }

    window.scroll({
      behavior: behavior,
      top: newPosition,
    })
  }

  // Months with no event are not rendered, so the current month may not be on the page —
  // walk forward to the first one that is.
  currentMonthOnPage() {
    const { months } = this.state

    for (let index = new Date().getMonth(); index < months.length; index++) {
      if (document.getElementById(months[index])) return months[index]
    }

    return null
  }

  // The page opens at the current month. Whatever happened while the list was still
  // loading says nothing about where the reader wants to be — the page was empty and
  // barely taller than the viewport — so the position is only asserted once the months
  // are on screen, and reasserted briefly against late layout shifts and the browser
  // restoring the previous scroll. The first real interaction hands control back.
  scrollToCurrentMonth() {
    if (window.location.hash) return

    const month = this.currentMonthOnPage()

    if (month == null) return

    const takeOverEvents = ['wheel', 'touchstart', 'keydown', 'mousedown']
    let takenOver = false
    const takeOver = () => {
      takenOver = true
    }
    const stopWatching = () =>
      takeOverEvents.forEach((name) => window.removeEventListener(name, takeOver))

    takeOverEvents.forEach((name) =>
      window.addEventListener(name, takeOver, { once: true, passive: true })
    )

    const deadline = Date.now() + 700
    const keepPosition = () => {
      if (takenOver) return stopWatching()

      this.scrollToMonth(month, 'auto')

      if (Date.now() < deadline) {
        window.requestAnimationFrame(keepPosition)
      } else {
        stopWatching()
      }
    }

    keepPosition()
  }

  onChange(event, action) {
    this.setState({
      loading: true,
      filters: this.buildFilterState(event, action),
    })

    const { initialEvents, filters } = this.state

    let events = initialEvents

    if (filters.name) events = this.filterByName(filters.name, events)
    if (filters.region) events = this.filterByRegion(filters.region, events)
    if (filters.championshipType)
      events = this.filterByChampionshipType(filters.championshipType, events)

    this.setState({
      filteredEvents: events,
      loading: false,
      hasFiltersApplied: true,
    })
  }

  clearFilters() {
    this.setState({
      filteredEvents: this.state.initialEvents,
      loading: false,
      hasFiltersApplied: false,
    })
    this.state.filters.name = ''
    this.state.filters.region = []
    this.state.filters.championshipType = []
  }

  buildFilterState(event, action) {
    let value = this.valueBy(event)
    let actionName = action ? action.name : 'name'

    const { filters } = this.state

    filters[actionName] = value

    return filters
  }

  valueBy(event) {
    if (!event) return

    if (event.target) return event.target.value

    return event.map((e) => e.value)
  }

  filterByName(keyword, events) {
    if (keyword.length < 3) return events

    const searchWords = '+' + keyword.trim().replace(/ /g, ' +')
    const searchResults = this.state.eventsIndex.search(searchWords)
    const ids = searchResults.map((r) => r.ref)

    return events.filter((event) => ids.includes(event.id.toString()))
  }

  filterByRegion(regions, events) {
    if (regions.length === 0) return events

    return events.filter((event) => regions.includes(event.region))
  }

  filterByChampionshipType(types, events) {
    if (types.length === 0) return events

    return events.filter((event) => types.every(type => event.eventGroups.includes(type)))
  }

  render() {
    return (
      <React.Fragment>
        <section className="row no-gutters">
          <div className="col-12">
            <h1 className="find-your-events">{i18n.t('calendar.title')}</h1>
          </div>
        </section>

        <Filter
          onChange={this.onChange.bind(this)}
          scrollToRef={this.scrollToRef.bind(this)}
          onClearFilters={this.clearFilters.bind(this)}
          champNames={this.state.filters.name}
          champTypes={this.state.filters.championshipType}
          champRegions={this.state.filters.region}
          resultsFound={this.state.filteredEvents.length}
          months={this.state.months}
          years={this.state.years}
          showRegion={this.state.showRegions}
          availableRegions={this.state.regionsWithEvents}
          availableChampTypes={this.state.availableChampTypes}
          hasFiltersApplied={this.state.hasFiltersApplied}
          i18n={i18n}
        />

        <div className="infosite-events no-gutters">
          <List
            initialEvents={this.state.initialEvents}
            filteredEvents={this.state.filteredEvents}
            months={this.state.months}
            years={this.state.years}
            loading={this.state.loading}
            i18n={i18n}
          />
        </div>

        <NoResults
          display={!this.state.loading && this.state.filteredEvents.length <= 0}
          resultCount={i18n.t('calendar.list.resultsCount', { count: 0 })}
          title={i18n.t('calendar.list.emptyResultsTitle')}
          message={i18n.t('calendar.list.emptyResultsMessage')}
          action={this.clearFilters.bind(this)}
          actionText={i18n.t('calendar.list.emptyResultsAction')}
        />
      </React.Fragment>
    )
  }
}

Calendar.propTypes = {
  events: PropTypes.array,
  months: PropTypes.arrayOf(PropTypes.string),
}

document.addEventListener('DOMContentLoaded', () => {
  ReactDOM.render(<Calendar />, document.querySelector('#calendar-container'))
})
